import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Users, UserPlus, Shield, Mail, Check, X, Trash2, Edit2, Send, Plus,
  Building2, Crown, User, Loader2, RefreshCw, ChevronRight, UserMinus, ArrowLeft,
  SlidersHorizontal, Repeat, Tag, CalendarDays, MoreHorizontal, Palette, Clock,
  Eye, Lock, Wallet, ArrowRight, Hourglass, Layers, CreditCard, Power,
} from 'lucide-react';
import apiService from '../services/api';
import tokenManager from '../utils/tokenManager';
import toast from 'react-hot-toast';
import PublicHolidaysSection from './PublicHoliday';
import LeavePoliciesSection from './LeavePolicies';
import BillingSection from './Billing';
import ShutdownsSection from './Shutdowns';
import {
  cx, fullName, unwrapList, errorMessage,
  UIStyles, Button, IconButton, Chip, Avatar, AvatarStack, EmptyState, SkeletonRows, Notice,
  SectionHeader, SettingRow, SaveBar, Toggle, Segmented, SearchInput, NumberInput, Dropdown, Menu,
  Modal, ModalHeader, ModalBody, ModalFooter, ModalIcon, ConfirmProvider, useConfirm,
  inputClass, textareaClass, labelClass,
} from './settingsUI';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
// One role vocabulary across the app: Super admin, Admin, Manager, Employee
const ROLES = [
  { value: 'super_admin', label: 'Super admin', icon: Crown,     color: '#8A5F00', desc: 'Everything, including billing' },
  { value: 'admin',       label: 'Admin',       icon: Shield,    color: 'var(--accent)', desc: 'Approves anyone, sees sick notes (HR), changes settings' },
  { value: 'manager',     label: 'Manager', icon: Building2, color: 'var(--warn-text)', desc: 'Approves and rejects leave for their department. Set per department in Departments.' },
  { value: 'member',      label: 'Employee',    icon: User,      color: 'var(--text-2)', desc: 'Books their own leave' },
];

const NAV_GROUPS = [
  {
    label: 'Leave',
    items: [
      { id: 'general',         label: 'General',         icon: SlidersHorizontal, desc: 'Leave year and defaults' },
      { id: 'leave-policies',  label: 'Leave policies',  icon: Layers,            desc: 'Allowances, accrual and groups' },
      { id: 'carry-forward',   label: 'Carry forward',   icon: Repeat,            desc: 'Unused allowance' },
      { id: 'leave-types',     label: 'Leave types',     icon: Tag,               desc: 'Holiday, sickness and more' },
      { id: 'public-holidays', label: 'Public holidays', icon: CalendarDays,      desc: 'By location' },
      { id: 'shutdowns',       label: 'Office shutdowns', icon: Power,            desc: 'Closures booked for everyone' },
    ],
  },
  {
    label: 'People',
    items: [
      { id: 'departments', label: 'Departments', icon: Building2, desc: 'Teams and their members' },
      { id: 'members',     label: 'Members',     icon: Users,     desc: 'Roles and allowances' },
      { id: 'invite',      label: 'Invite',      icon: UserPlus,  desc: 'Bring new people in' },
      { id: 'roles',       label: 'Roles',       icon: Shield,    desc: 'What each role can do' },
    ],
  },
  {
    label: 'Company',
    items: [
      { id: 'billing', label: 'Billing', icon: CreditCard, desc: 'Plan and Trials ' },
    ],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap(g => g.items);

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const PRESET_COLORS = [
  '#FFC81E', '#FFEFB3', '#F5A300', '#C98A00',
  '#E3DCCB', '#6FCF97', '#FF8A80', '#FFE08A',
  '#FFD452', '#E0B000', '#F2C14E', '#DEAE1C',
  '#EFE4CB', '#D9C9A3', '#B5A889', '#FFF1C2',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const roleInfo = role => ROLES.find(r => r.value === role) || ROLES[3];

// "1 January" style label for a day/month, using a non-leap reference year
function dayMonthLabel(date) {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

function leaveYearEnd(startMonth, startDay) {
  return new Date(2001, startMonth - 1, startDay - 1);
}

function formatShortDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function plural(n, word, pluralWord = `${word}s`) {
  return `${n} ${n === 1 ? word : pluralWord}`;
}

// ─────────────────────────────────────────────────────────────
// Badges
// ─────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const info = roleInfo(role);
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: `${info.color}14`, color: info.color }}
    >
      <info.icon size={10} /> {info.label}
    </span>
  );
}

function UnitBadge({ unit }) {
  return unit === 'hours'
    ? <Chip tone="violet"><Clock size={10} /> Hours</Chip>
    : <Chip tone="ink"><CalendarDays size={10} /> Days</Chip>;
}

// ─────────────────────────────────────────────────────────────
// useCompanySettings hook
// ─────────────────────────────────────────────────────────────
function useCompanySettings() {
  const [settings, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await apiService.getCompanySettings();
      setSettings(res?.data ?? res);
    } catch {
      toast.error('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (updates) => {
    try {
      const res  = await apiService.updateCompanySettings(updates);
      const data = res?.data ?? res;
      setSettings(data);
      toast.success('Settings saved.');
      return data;
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save settings.'));
      throw err;
    }
  }, []);

  return { settings, loading, save };
}

// Form state that knows whether it differs from what's saved
function useSettingsForm(initial) {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = key => value => setForm(f => ({ ...f, [key]: value }));
  const dirty = Object.keys(initial).some(k => initial[k] !== form[k]);
  return { form, set, dirty, reset: () => setForm(initial) };
}

// ═════════════════════════════════════════════════════════════
// GENERAL
// ═════════════════════════════════════════════════════════════
function LeaveYearPreview({ startMonth, startDay }) {
  const months = Array.from({ length: 12 }, (_, i) => (startMonth - 1 + i) % 12);
  const end = leaveYearEnd(startMonth, startDay);
  const start = new Date(2001, startMonth - 1, startDay);
  const currentMonth = new Date().getMonth();

  return (
    <div className="ph-fade rounded-2xl bg-ink-50 p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink/70">Your leave year</div>
          <div className="mt-0.5 text-base font-semibold text-sand-900">
            {dayMonthLabel(start)} <ArrowRight size={14} className="mx-1 inline -translate-y-px text-sand-500" /> {dayMonthLabel(end)}
          </div>
        </div>
        <span className="text-[13px] text-sand-600">Allowances reset on {dayMonthLabel(start)}</span>
      </div>
      <div className="mt-4 grid grid-cols-12 gap-1">
        {months.map((m, i) => (
          <div key={m} className="text-center">
            <div
              className={cx(
                'h-2.5 rounded-full transition-all',
                i === 0 ? 'bg-ink' : m === currentMonth ? 'bg-emerald-500' : 'bg-ink/20',
              )}
              title={MONTH_NAMES[m]}
            />
            <div className={cx('mt-1.5 text-[10px] font-semibold', i === 0 ? 'text-ink' : 'text-sand-500')}>
              {MONTH_NAMES[m][0]}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-sand-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-ink" /> Year starts</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> This month</span>
      </div>
    </div>
  );
}

function GeneralSection({ settings, onSave }) {
  const initial = useMemo(() => ({
    startMonth:  settings.leaveYearStartMonth ?? 1,
    startDay:    settings.leaveYearStartDay ?? 1,
    defaultDays: settings.defaultAnnualLeaveDays ?? 25,
    workingDays: settings.workingDaysPerWeek ?? 5,
    breakMinutes:    Number(settings.customBreakMinutes ?? 0),
    breakAfterHours: Number(settings.customBreakAfterHours ?? 6),
  }), [settings]);
  const { form, set, dirty, reset } = useSettingsForm(initial);
  const [saving, setSaving] = useState(false);

  const monthOptions = MONTH_NAMES.map((m, i) => ({ value: i + 1, label: m }));
  const dayOptions   = Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
  const weeks = form.workingDays > 0 ? Math.round((form.defaultDays / form.workingDays) * 10) / 10 : 0;

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        leaveYearStartMonth:    form.startMonth,
        leaveYearStartDay:      form.startDay,
        defaultAnnualLeaveDays: form.defaultDays,
        workingDaysPerWeek:     form.workingDays,
        customBreakMinutes:     form.breakMinutes,
        customBreakAfterHours:  form.breakAfterHours,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <SectionHeader title="General" description="Core leave year configuration. Allowance units are set per individual member." />
      <div className="max-w-3xl">
        <LeaveYearPreview startMonth={form.startMonth} startDay={form.startDay} />

        <div className="mt-6">
          <SettingRow label="Leave year start" hint="The date allowances reset each year.">
            <div className="flex gap-2">
              <Dropdown className="w-40" options={monthOptions} value={form.startMonth} onChange={set('startMonth')} placeholder="Month" />
              <Dropdown className="w-20" options={dayOptions} value={form.startDay} onChange={set('startDay')} placeholder="Day" />
            </div>
          </SettingRow>
          <SettingRow
            label="Default annual leave"
            hint={<>Applied when adding new members. That's about <span className="font-semibold text-sand-900">{weeks} weeks</span> off a year.</>}
          >
            <NumberInput value={form.defaultDays} onChange={set('defaultDays')} min={0} max={365} unit="days" />
          </SettingRow>
          <SettingRow label="Working days per week" hint="Used to work out how many days a leave request takes.">
            <div className="flex flex-col items-start gap-2">
              <NumberInput value={form.workingDays} onChange={set('workingDays')} min={1} max={7} unit="days" />
              <div className="flex gap-1" aria-hidden="true">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <span
                    key={i}
                    className={cx(
                      'flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-bold transition-colors',
                      i < form.workingDays ? 'bg-ink text-[color:var(--on-accent)]' : 'bg-sand-100 text-sand-500',
                    )}
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </SettingRow>
          <SettingRow
            label="Unpaid break on custom hours"
            hint={form.breakMinutes > 0
              ? <>Taken off {form.breakAfterHours > 0 ? `custom-hours bookings longer than ${form.breakAfterHours} hours` : 'every custom-hours booking'}, e.g. 9:00–17:00 counts as <span className="font-semibold text-sand-900">{Math.round((8 - form.breakMinutes / 60) * 100) / 100}h</span>. People can change it when they book.</>
              : 'Custom-hours bookings count every hour from start to end.'}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Dropdown className="w-36" options={BREAK_CHOICES} value={form.breakMinutes} onChange={set('breakMinutes')} />
              {form.breakMinutes > 0 && (
                <>
                  <span className="text-sm text-sand-600">for</span>
                  <Dropdown className="w-52" options={BREAK_AFTER_CHOICES} value={form.breakAfterHours} onChange={set('breakAfterHours')} />
                </>
              )}
            </div>
          </SettingRow>
        </div>

        <SaveBar onSave={handleSave} onDiscard={reset} saving={saving} dirty={dirty} />
      </div>
    </div>
  );
}

const BREAK_CHOICES = [
  { value: 0, label: 'No break' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1½ hours' },
];
const BREAK_AFTER_CHOICES = [0, 4, 5, 6, 7, 8].map(h => ({ value: h, label: h === 0 ? 'all bookings' : `bookings over ${h} hours` }));

// ═════════════════════════════════════════════════════════════
// CARRY FORWARD
// ═════════════════════════════════════════════════════════════
function CarryForwardSection({ settings, onSave }) {
  const initial = useMemo(() => ({
    daysMax:      settings.carryForwardDaysMax ?? 5,
    hoursMax:     settings.carryForwardHoursMax ?? 0,
    expires:      settings.carryForwardExpires ?? false,
    expiryMonths: settings.carryForwardExpiryMonths ?? 3,
  }), [settings]);
  const { form, set, dirty, reset } = useSettingsForm(initial);
  const [saving, setSaving] = useState(false);

  const startMonth = settings.leaveYearStartMonth ?? 1;
  const startDay   = settings.leaveYearStartDay ?? 1;
  const expiryDate = new Date(2001, startMonth - 1 + Number(form.expiryMonths || 0), startDay - 1);
  const nothingCarries = !form.daysMax && !form.hoursMax;

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        carryForwardDaysMax:      form.daysMax,
        carryForwardHoursMax:     form.hoursMax,
        carryForwardExpires:      form.expires,
        carryForwardExpiryMonths: form.expires ? form.expiryMonths : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <SectionHeader title="Carry forward" description="How much unused allowance can be transferred into the next leave year." />
      <div className="max-w-3xl">
        {/* Plain-English summary */}
        <div className="ph-fade flex items-start gap-4 rounded-2xl bg-ink-50 p-4 sm:p-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ink text-[color:var(--on-accent)]">
            <Repeat size={19} />
          </span>
          <div className="min-w-0 text-sm leading-relaxed text-sand-700">
            {nothingCarries ? (
              <><span className="font-semibold text-sand-900">Nothing carries over.</span> Unused leave is lost when the leave year ends.</>
            ) : (
              <>
                At the end of each leave year, people can keep up to{' '}
                {form.daysMax > 0 && <span className="font-semibold text-sand-900">{plural(form.daysMax, 'day')}</span>}
                {form.daysMax > 0 && form.hoursMax > 0 && ' or '}
                {form.hoursMax > 0 && <span className="font-semibold text-sand-900">{plural(form.hoursMax, 'hour')}</span>}
                {' '}of unused leave.{' '}
                {form.expires
                  ? <>It must be used by <span className="font-semibold text-sand-900">{dayMonthLabel(expiryDate)}</span>.</>
                  : 'It never expires.'}
              </>
            )}
          </div>
        </div>

        <div className="mt-6">
          <SettingRow label="Members with allowances in days" hint="The most unused days someone can take into the next year.">
            <NumberInput value={form.daysMax} onChange={set('daysMax')} min={0} max={365} unit="days (max)" />
          </SettingRow>
          <SettingRow label="Members with allowances in hours" hint="The most unused hours someone can take into the next year.">
            <NumberInput value={form.hoursMax} onChange={set('hoursMax')} min={0} max={9999} unit="hours (max)" />
          </SettingRow>
          <SettingRow label="Carried-forward leave expires" hint="Turn on to make carried-forward leave run out part-way through the new year.">
            <Toggle checked={form.expires} onChange={set('expires')} label={form.expires ? 'Yes' : 'Never'} />
          </SettingRow>
          {form.expires && (
            <div className="ph-rise">
              <SettingRow label="Expires after" hint={`Months into the new leave year — that's ${dayMonthLabel(expiryDate)}.`}>
                <NumberInput value={form.expiryMonths} onChange={set('expiryMonths')} min={1} max={12} unit="months" />
              </SettingRow>
            </div>
          )}
        </div>

        <SaveBar onSave={handleSave} onDiscard={reset} saving={saving} dirty={dirty} />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// LEAVE TYPES
// ═════════════════════════════════════════════════════════════
const LEAVE_RULES = [
  { key: 'affectsBalance',   label: 'Deducts from annual allowance',      hint: 'Days taken come off the person’s balance' },
  { key: 'requiresApproval', label: 'Needs manager approval',             hint: 'Requests wait for a manager before they’re booked' },
  { key: 'countsAsAbsence',  label: 'Counts as time off',                 hint: 'Off = not an absence (working from home, on-site training): left out of “out today” and absence counts' },
  { key: 'isPaid',           label: 'Paid leave',                         hint: 'Shown as paid on reports' },
];

function ColorPicker({ value, onChange }) {
  return (
    <div>
      <label className={labelClass}>Colour</label>
      <div className="flex flex-wrap items-center gap-2">
        {PRESET_COLORS.map(color => {
          const on = value?.toLowerCase() === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              title={color}
              aria-label={`Colour ${color}`}
              className={cx(
                'flex h-8 w-8 items-center justify-center rounded-lg transition-all hover:scale-110',
                on && 'ring-2 ring-sand-900 ring-offset-2',
              )}
              style={{ background: color }}
            >
              {on && <Check size={14} className="text-[#1C1A14]" />}
            </button>
          );
        })}
        <label
          className="relative flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-sand-300 px-2.5 text-xs font-medium text-sand-600 transition-colors hover:border-sand-500 hover:text-sand-900"
          title="Pick a custom colour"
        >
          <Palette size={13} /> Custom
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
        </label>
      </div>
    </div>
  );
}

function LeaveTypeChips({ type }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Chip tone={type.isPaid ? 'green' : 'red'}>{type.isPaid ? 'Paid' : 'Unpaid'}</Chip>
      <Chip tone={type.affectsBalance ? 'ink' : 'sand'}>{type.affectsBalance ? 'Deductible' : 'Non-deductible'}</Chip>
      {type.requiresApproval !== undefined && (
        <Chip tone={type.requiresApproval ? 'amber' : 'sand'}>{type.requiresApproval ? 'Needs approval' : 'Auto-approved'}</Chip>
      )}
      {type.isPrivate && <Chip tone="sand"><Lock size={10} /> Private</Chip>}
      {(type.countsAsAbsence ?? type.countsAbsent) === false && <Chip tone="sand">Not time off</Chip>}
      {type.entitlementDays != null && type.entitlementDays !== '' && type.hasAllowance !== false && (
        <Chip tone="amber">{Number(type.entitlementDays)} days / {type.entitlementCycleMonths ?? 12} months</Chip>
      )}
      {type.documentRule && <Chip tone="sand">Sick note rule</Chip>}
      {type.parentalCap && <Chip tone="sand">{type.parentalCap.months}m {type.parentalCap.days}d per child</Chip>}
    </div>
  );
}

function LeaveTypeModal({ leaveType, onClose, onSaved }) {
  const editing = !!leaveType;
  const [form, setForm] = useState({
    name:             leaveType?.name || '',
    color:            leaveType?.color || '#FFC81E',
    calendarAs:       leaveType?.calendarAs || 'busy',
    isPaid:           leaveType?.isPaid ?? true,
    affectsBalance:   leaveType?.affectsBalance ?? true,
    requiresApproval: leaveType?.requiresApproval ?? true,
    countsAbsent:     leaveType?.countsAbsent ?? true,
    countsAsAbsence:  (leaveType?.countsAsAbsence ?? leaveType?.countsAbsent) ?? true,
    isPrivate:        leaveType?.isPrivate ?? false,
    isActive:         leaveType?.isActive ?? true,
    // Own allowance with a cycle (e.g. ZA sick leave 30 days / 36 months)
    hasAllowance:     leaveType?.entitlementDays != null,
    entitlementDays:  leaveType?.entitlementDays ?? '',
    entitlementCycleMonths: leaveType?.entitlementCycleMonths ?? 12,
    entitlementScale: leaveType?.entitlementScale ?? 'none',
    // Supporting document (sick note)
    needsDocument:    !!leaveType?.documentRule,
    docAfterDays:     leaveType?.documentRule?.afterDays ?? 2,
    docRepeatCount:   leaveType?.documentRule?.repeatCount ?? 3,
    docRepeatWeeks:   leaveType?.documentRule?.repeatWeeks ?? 8,
    // Parental cap per child, shared between parents
    hasParentalCap:   !!leaveType?.parentalCap,
    capMonths:        leaveType?.parentalCap?.months ?? 4,
    capDays:          leaveType?.parentalCap?.days ?? 10,
  });
  const [saving, setSaving] = useState(false);
  const set = key => value => setForm(f => ({ ...f, [key]: value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Leave type name is required'); return; }
    setSaving(true);
    try {
      const { hasAllowance, needsDocument, hasParentalCap, docAfterDays, docRepeatCount, docRepeatWeeks, capMonths, capDays, ...rest } = form;
      const payload = {
        ...rest,
        name: form.name.trim(),
        countsAbsent: form.countsAsAbsence,
        entitlementDays: hasAllowance && form.entitlementDays !== '' ? Number(form.entitlementDays) : null,
        entitlementCycleMonths: hasAllowance ? Number(form.entitlementCycleMonths) || 12 : null,
        entitlementScale: hasAllowance ? form.entitlementScale : 'none',
        documentRule: needsDocument ? { afterDays: Number(docAfterDays) || 0, repeatCount: Number(docRepeatCount) || null, repeatWeeks: Number(docRepeatWeeks) || null } : null,
        parentalCap: hasParentalCap ? { months: Number(capMonths) || 0, days: Number(capDays) || 0 } : null,
      };
      if (editing) {
        const res = await apiService.updateLeaveType(leaveType.id, payload);
        toast.success('Leave type updated');
        onSaved(res?.data ?? res);
      } else {
        const res = await apiService.createLeaveType({ ...payload, isActive: true });
        toast.success('Leave type added');
        onSaved(res?.data ?? res);
      }
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, editing ? 'Failed to update leave type' : 'Failed to add leave type'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-xl" z={200} labelledBy="lt-modal-title">
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <ModalHeader
          id="lt-modal-title"
          title={editing ? 'Edit leave type' : 'Add leave type'}
          subtitle="Set how this leave behaves and how it looks on the calendar"
          onClose={onClose}
          leading={<ModalIcon icon={Tag} />}
        />
        <ModalBody className="space-y-5">
          {/* Live preview */}
          <div className="rounded-2xl border border-sand-200 bg-sand-50 p-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">Preview</div>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-[#1C1A14] shadow-sm transition-colors"
                style={{ background: form.color }}
              >
                <CalendarDays size={14} /> {form.name.trim() || 'Leave type'}
              </span>
              <LeaveTypeChips type={form} />
            </div>
          </div>

          <div>
            <label htmlFor="lt-name" className={labelClass}>Name</label>
            <input
              id="lt-name"
              type="text"
              value={form.name}
              onChange={e => set('name')(e.target.value)}
              placeholder="e.g. Holiday, Sick leave, Study day"
              autoFocus
              className={inputClass}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Show in calendar as</label>
              <Segmented
                size="md"
                className="flex w-full"
                value={form.calendarAs}
                onChange={set('calendarAs')}
                options={[
                  { value: 'busy', label: 'Busy' },
                  { value: 'free', label: 'Free' },
                  { value: 'out_of_office', label: 'Away' , title: 'Out of office' },
                ]}
              />
            </div>
            <div>
              <label className={labelClass}>Visibility</label>
              <Segmented
                size="md"
                className="flex w-full"
                // Private: colleagues only ever see "Away"; the person, their
                // approver and admins see the type (saved as isPrivate)
                value={form.isPrivate ? 'private' : 'public'}
                onChange={v => set('isPrivate')(v === 'private')}
                options={[
                  { value: 'private', label: 'Private', icon: Lock },
                  { value: 'public', label: 'Public', icon: Eye },
                ]}
              />
            </div>
          </div>

          <div className="divide-y divide-sand-100 rounded-2xl border border-sand-200">
            {editing && (
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sand-900">Active</div>
                  <div className="text-xs text-sand-600">Inactive types can't be chosen for new requests</div>
                </div>
                <Toggle checked={form.isActive} onChange={set('isActive')} label={form.isActive ? 'Yes' : 'No'} />
              </div>
            )}
            {LEAVE_RULES.map(rule => (
              <div key={rule.key} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sand-900">{rule.label}</div>
                  <div className="text-xs text-sand-600">{rule.hint}</div>
                </div>
                <Toggle checked={form[rule.key]} onChange={set(rule.key)} label={form[rule.key] ? 'Yes' : 'No'} />
              </div>
            ))}
          </div>

          {/* Statutory rules: own allowance, sick notes, parental cap */}
          <div className="divide-y divide-sand-100 rounded-2xl border border-sand-200">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sand-900">Its own allowance</div>
                  <div className="text-xs text-sand-600">e.g. sick leave 30 days over 36 months, family responsibility 3 days a year (from each person's start date)</div>
                </div>
                <Toggle checked={form.hasAllowance} onChange={set('hasAllowance')} label={form.hasAllowance ? 'Yes' : 'No'} />
              </div>
              {form.hasAllowance && (
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <label className="text-xs text-sand-600">Days
                    <input type="number" min={0} step={0.5} className={inputClass} value={form.entitlementDays} onChange={e => set('entitlementDays')(e.target.value)} />
                  </label>
                  <label className="text-xs text-sand-600">Every (months)
                    <input type="number" min={1} max={120} className={inputClass} value={form.entitlementCycleMonths} onChange={e => set('entitlementCycleMonths')(e.target.value)} />
                  </label>
                  <label className="text-xs text-sand-600">Part-timers
                    <select className={inputClass} value={form.entitlementScale} onChange={e => set('entitlementScale')(e.target.value)}>
                      <option value="none">Same days</option>
                      <option value="work_week">Pro rata to days worked</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sand-900">Needs a supporting document</div>
                  <div className="text-xs text-sand-600">A sick note is asked for and only HR (admins) can open it</div>
                </div>
                <Toggle checked={form.needsDocument} onChange={set('needsDocument')} label={form.needsDocument ? 'Yes' : 'No'} />
              </div>
              {form.needsDocument && (
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <label className="text-xs text-sand-600">After more than (days in a row)
                    <input type="number" min={0} max={30} className={inputClass} value={form.docAfterDays} onChange={e => set('docAfterDays')(e.target.value)} />
                  </label>
                  <label className="text-xs text-sand-600">Or the Nth absence…
                    <input type="number" min={0} max={20} className={inputClass} value={form.docRepeatCount} onChange={e => set('docRepeatCount')(e.target.value)} />
                  </label>
                  <label className="text-xs text-sand-600">…within (weeks)
                    <input type="number" min={0} max={52} className={inputClass} value={form.docRepeatWeeks} onChange={e => set('docRepeatWeeks')(e.target.value)} />
                  </label>
                </div>
              )}
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sand-900">Parental leave limit per child</div>
                  <div className="text-xs text-sand-600">Shared between the parents (birth, adoption or surrogacy). South Africa: 4 months and 10 days</div>
                </div>
                <Toggle checked={form.hasParentalCap} onChange={set('hasParentalCap')} label={form.hasParentalCap ? 'Yes' : 'No'} />
              </div>
              {form.hasParentalCap && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs text-sand-600">Months
                    <input type="number" min={0} max={24} className={inputClass} value={form.capMonths} onChange={e => set('capMonths')(e.target.value)} />
                  </label>
                  <label className="text-xs text-sand-600">Plus days
                    <input type="number" min={0} max={60} className={inputClass} value={form.capDays} onChange={e => set('capDays')(e.target.value)} />
                  </label>
                </div>
              )}
            </div>
          </div>

          <ColorPicker value={form.color} onChange={set('color')} />
        </ModalBody>
        <ModalFooter className="justify-end">
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" icon={Check} loading={saving}>
            {editing ? 'Save changes' : 'Create leave type'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function LeaveTypesSection() {
  const confirm = useConfirm();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null); // null | 'new' | leaveType
  const [filter, setFilter]         = useState('all');
  const [togglingId, setTogglingId] = useState(null);

  const loadLeaveTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getLeaveTypes();
      const data = res?.data ?? res;
      setLeaveTypes(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeaveTypes(); }, [loadLeaveTypes]);

  async function handleDelete(type) {
    const ok = await confirm({
      title: `Delete ${type.name}?`,
      message: 'People won’t be able to request this type of leave any more.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteLeaveType(type.id);
      setLeaveTypes(prev => prev.filter(t => t.id !== type.id));
      toast.success('Leave type deleted');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete leave type'));
    }
  }

  async function handleToggleActive(type) {
    const next = !type.isActive;
    setTogglingId(type.id);
    setLeaveTypes(prev => prev.map(t => (t.id === type.id ? { ...t, isActive: next } : t)));
    try {
      await apiService.updateLeaveType(type.id, {
        name: type.name, color: type.color, isPaid: type.isPaid, affectsBalance: type.affectsBalance,
        requiresApproval: type.requiresApproval, calendarAs: type.calendarAs,
        countsAsAbsence: type.countsAsAbsence, isPrivate: type.isPrivate, entitlementDays: type.entitlementDays,
        entitlementCycleMonths: type.entitlementCycleMonths, entitlementScale: type.entitlementScale,
        documentRule: type.documentRule, parentalCap: type.parentalCap,
        countsAbsent: type.countsAbsent, isActive: next,
      });
      toast.success(`${type.name} ${next ? 'activated' : 'deactivated'}`);
    } catch (err) {
      setLeaveTypes(prev => prev.map(t => (t.id === type.id ? { ...t, isActive: !next } : t)));
      toast.error(errorMessage(err, 'Failed to update leave type'));
    } finally {
      setTogglingId(null);
    }
  }

  const activeCount = leaveTypes.filter(t => t.isActive).length;
  const visible = leaveTypes.filter(t => filter === 'all' || (filter === 'active' ? t.isActive : !t.isActive));

  return (
    <div>
      <SectionHeader
        title="Leave types"
        description="Manage the types of leave available in your company."
        action={<Button variant="primary" icon={Plus} onClick={() => setModal('new')}>Add leave type</Button>}
      />

      {loading ? (
        <SkeletonRows rows={4} height="h-[72px]" />
      ) : leaveTypes.length === 0 ? (
        <EmptyState icon={Tag} title="No leave types yet" message="Add the kinds of leave people can request, like holiday or sickness.">
          <Button variant="primary" icon={Plus} onClick={() => setModal('new')}>Add your first leave type</Button>
        </EmptyState>
      ) : (
        <>
          {activeCount !== leaveTypes.length && (
            <Segmented
              className="mb-4"
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All', count: leaveTypes.length },
                { value: 'active', label: 'Active', count: activeCount },
                { value: 'inactive', label: 'Inactive', count: leaveTypes.length - activeCount },
              ]}
            />
          )}
          <div className="space-y-2">
            <style>{`.lt-card { position: relative; } .lt-card:hover, .lt-card:focus-within { z-index: 20; }`}</style>
            {visible.map(type => (
              <div
                key={type.id}
                role="button"
                tabIndex={0}
                onClick={() => setModal(type)}
                onKeyDown={e => { if (e.key === 'Enter') setModal(type); }}
                className={cx(
                  // lt-card: raised while hovered/focused so the ⋯ menu sits above the next card
                  'lt-card ph-fade group flex cursor-pointer flex-wrap items-center gap-4 rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-4 transition-all',
                  'hover:border-sand-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30',
                  !type.isActive && 'opacity-60',
                )}
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-base font-bold text-[#1C1A14] shadow-sm transition-transform group-hover:scale-105"
                  style={{ background: type.color || '#FFC81E' }}
                >
                  {(type.name || '?')[0].toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold text-sand-900">{type.name}</div>
                  <div className="mt-1.5"><LeaveTypeChips type={type} /></div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Toggle
                    checked={!!type.isActive}
                    onChange={() => handleToggleActive(type)}
                    disabled={togglingId === type.id}
                    label={type.isActive ? 'Active' : 'Off'}
                  />
                  <Menu
                    trigger={({ toggle }) => <IconButton label="More actions" onClick={toggle}><MoreHorizontal size={17} /></IconButton>}
                    items={[
                      { label: 'Edit', icon: Edit2, onClick: () => setModal(type) },
                      { label: 'Delete', icon: Trash2, tone: 'danger', onClick: () => handleDelete(type) },
                    ]}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setModal('new')}
              className="group flex w-full items-center gap-4 rounded-2xl border-2 border-dashed border-sand-200 p-4 text-left transition-all hover:border-ink/40 hover:bg-ink-50/40"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sand-100 text-sand-600 transition-all group-hover:bg-ink group-hover:text-[color:var(--on-accent)]">
                <Plus size={18} />
              </span>
              <span className="text-sm font-semibold text-sand-700 group-hover:text-ink">Add a new leave type</span>
            </button>
          </div>
        </>
      )}

      {modal && (
        <LeaveTypeModal
          leaveType={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={saved => {
            if (!saved?.id) { loadLeaveTypes(); return; }
            setLeaveTypes(prev => (prev.some(t => t.id === saved.id) ? prev.map(t => (t.id === saved.id ? saved : t)) : [...prev, saved]));
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// DEPARTMENTS
// ═════════════════════════════════════════════════════════════
// Same tile in every theme: soft tint behind an ink (light) / yellow (dark) icon
function DepartmentIcon({ size = 44 }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-xl"
      style={{ width: size, height: size, background: 'var(--tint)', color: 'var(--accent)' }}
    >
      <Building2 size={Math.round(size * 0.45)} />
    </span>
  );
}

function DepartmentFormModal({ department, onClose, onSaved }) {
  const [name, setName]               = useState(department?.name || '');
  const [description, setDescription] = useState(department?.description || '');
  const [saving, setSaving]           = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Department name is required'); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() };
      const res = department
        ? await apiService.updateDepartment(department.id, body)
        : await apiService.createDepartment(body);
      toast.success(department ? 'Department updated' : 'Department created');
      onSaved(res?.data ?? res);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save department'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-md" z={250} labelledBy="dept-form-title">
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <ModalHeader
          id="dept-form-title"
          title={department ? 'Edit department' : 'New department'}
          subtitle="Group people who work together"
          onClose={onClose}
          leading={<DepartmentIcon name={name || 'New'} size={40} />}
        />
        <ModalBody className="space-y-4">
          <div>
            <label htmlFor="dept-name" className={labelClass}>Name</label>
            <input id="dept-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Engineering, HR, Sales" autoFocus className={inputClass} />
          </div>
          <div>
            <label htmlFor="dept-desc" className={labelClass}>
              Description <span className="font-normal text-sand-500">(optional)</span>
            </label>
            <textarea id="dept-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this team do?" rows={3} className={textareaClass} />
          </div>
        </ModalBody>
        <ModalFooter className="justify-end">
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" icon={Check} loading={saving}>
            {department ? 'Save changes' : 'Create department'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function AddDepartmentMemberModal({ department, existingMemberIds, onClose, onSaved }) {
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(new Set());
  const [role, setRole]         = useState('member');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiService.getCompanyUsers();
        setAllUsers(unwrapList(res, 'users'));
      } catch {
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const available = allUsers.filter(u => {
    if (existingMemberIds.includes(u.id)) return false;
    const q = search.trim().toLowerCase();
    return !q || fullName(u).toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function handleAdd() {
    if (!selected.size) return;
    setSaving(true);
    let added = 0;
    const failed = [];
    for (const id of selected) {
      const user = allUsers.find(u => u.id === id);
      try {
        await apiService.addDepartmentMember(department.id, id, role);
        added++;
      } catch (err) {
        failed.push(user ? fullName(user) : 'someone');
      }
    }
    setSaving(false);
    if (added) toast.success(`${plural(added, 'person', 'people')} added to ${department.name}`);
    if (failed.length) toast.error(`Couldn't add ${failed.join(', ')}`);
    onSaved();
    if (!failed.length) onClose();
  }

  return (
    <Modal onClose={onClose} className="max-w-lg" z={300} labelledBy="dept-add-title">
      <ModalHeader
        id="dept-add-title"
        title="Add people"
        subtitle={`To ${department.name}`}
        onClose={onClose}
        leading={<ModalIcon icon={UserPlus} />}
      />
      <div className="px-5 pt-3 sm:px-6">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email…" autoFocus />
      </div>
      <ModalBody className="pt-3">
        <div className="rounded-xl border border-sand-200 p-1">
          {loading ? (
            <div className="p-2"><SkeletonRows rows={4} height="h-12" /></div>
          ) : available.length === 0 ? (
            <div className="py-10 text-center text-sm text-sand-500">
              {search ? 'No one matches your search' : 'Everyone is already in this department'}
            </div>
          ) : (
            available.map(u => {
              const on = selected.has(u.id);
              return (
                <label
                  key={u.id}
                  className={cx('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors', on ? 'bg-ink-50' : 'hover:bg-sand-50')}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(u.id)} className="h-4 w-4 shrink-0 accent-ink" />
                  <Avatar person={u} size={32} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-sand-900">{fullName(u)}</span>
                    <span className="block truncate text-xs text-sand-500">{u.email}</span>
                  </span>
                  <RoleBadge role={u.role} />
                </label>
              );
            })
          )}
        </div>
      </ModalBody>
      <div className="px-5 pt-2 sm:px-6">
        <label className={labelClass}>Role in department</label>
        <Segmented
          size="md"
          className="flex w-full"
          value={role}
          onChange={setRole}
          options={[
            { value: 'member', label: 'Employee', icon: User },
            { value: 'manager', label: 'Manager', icon: Building2 },
          ]}
        />
        <p className="m-0 mt-1.5 text-xs text-sand-500">Managers approve and reject leave for everyone in this department.</p>
      </div>
      <ModalFooter>
        <span className="text-[13px] text-sand-600">{selected.size ? `${plural(selected.size, 'person', 'people')} selected` : 'Pick one or more people'}</span>
        <div className="ml-auto flex gap-2">
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" icon={UserPlus} loading={saving} disabled={!selected.size} onClick={handleAdd}>
            Add{selected.size ? ` ${selected.size}` : ''}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

function DepartmentDetail({ department, onBack, onDepartmentUpdated }) {
  const confirm = useConfirm();
  const [members, setMembers]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingDept, setEditingDept]     = useState(false);
  const [search, setSearch]               = useState('');

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getDepartmentMembers(department.id);
      const list = res?.data ?? res;
      setMembers(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [department.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  async function handleRemove(userId, name) {
    const ok = await confirm({
      title: `Remove ${name}?`,
      message: `${name} will no longer be part of ${department.name}. Their account isn't affected.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.removeDepartmentMember(department.id, userId);
      toast.success(`${name} removed`);
      loadMembers();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to remove member'));
    }
  }

  async function handleRoleChange(userId, newRole) {
    setMembers(prev => prev.map(m => ((m.user ?? m).id === userId ? { ...m, role: newRole } : m)));
    try {
      await apiService.updateDepartmentMemberRole(department.id, userId, newRole);
      toast.success(newRole === 'manager' ? 'Now a department admin — they can approve leave here' : 'Now a member');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to update role'));
      loadMembers();
    }
  }

  const existingIds = members.map(m => m.id ?? m.userId);
  // Department admins (stored as 'manager'; 'admin' is treated the same)
  const managerCount = members.filter(m => m.role === 'manager' || m.role === 'admin').length;
  const q = search.trim().toLowerCase();
  const visible = members.filter(m => {
    const u = m.user ?? m;
    return !q || fullName(u).toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  return (
    <div className="ph-rise">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-sand-600 transition-colors hover:bg-sand-100 hover:text-sand-900"
      >
        <ArrowLeft size={14} /> All departments
      </button>

      <SectionHeader
        leading={<DepartmentIcon name={department.name} size={48} />}
        title={department.name}
        description={department.description || 'No description yet'}
        action={
          <>
            <Button icon={Edit2} onClick={() => setEditingDept(true)}>Edit</Button>
            <Button variant="primary" icon={UserPlus} onClick={() => setShowAddMember(true)}>Add people</Button>
          </>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="rounded-xl bg-sand-50 px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">Members</div>
          <div className="mt-0.5 text-xl font-semibold text-sand-900">{loading ? '–' : members.length}</div>
        </div>
        <div className="rounded-xl bg-sand-50 px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">Managers</div>
          <div className="mt-0.5 text-xl font-semibold text-sand-900">{loading ? '–' : managerCount}</div>
        </div>
      </div>

      {members.length > 6 && (
        <SearchInput value={search} onChange={setSearch} placeholder="Search members…" className="mb-3 sm:max-w-sm" />
      )}

      {loading ? (
        <SkeletonRows rows={4} />
      ) : members.length === 0 ? (
        <EmptyState icon={Users} title="No one here yet" message="Add people to this department to group them on the calendar and in approvals.">
          <Button variant="primary" icon={UserPlus} onClick={() => setShowAddMember(true)}>Add the first person</Button>
        </EmptyState>
      ) : (
        <div className="divide-y divide-sand-100 rounded-2xl border border-sand-200">
          {visible.map((m, i) => {
            const user = m.user ?? m;
            const name = fullName(user);
            const role = m.role === 'admin' ? 'manager' : (m.role ?? 'member');
            return (
              <div key={user.id ?? i} className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-sand-50 sm:flex-nowrap">
                <Avatar person={user} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-sand-900">{name}</span>
                    {role === 'manager' && <Chip tone="green" title="Approves leave for this department"><Building2 size={10} /> Manager</Chip>}
                  </div>
                  <div className="truncate text-xs text-sand-500">{user.email}</div>
                </div>
                <Segmented
                  value={role}
                  onChange={newRole => newRole !== role && handleRoleChange(user.id, newRole)}
                  options={[
                    { value: 'member', label: 'Employee' },
                    { value: 'manager', label: 'Manager', title: 'Approves leave for this department' },
                  ]}
                />
                <IconButton label={`Remove ${name}`} tone="danger" onClick={() => handleRemove(user.id, name)}>
                  <UserMinus size={15} />
                </IconButton>
              </div>
            );
          })}
          {visible.length === 0 && <div className="py-10 text-center text-sm text-sand-500">No members match “{search}”</div>}
        </div>
      )}

      {showAddMember && (
        <AddDepartmentMemberModal
          department={department}
          existingMemberIds={existingIds}
          onClose={() => setShowAddMember(false)}
          onSaved={loadMembers}
        />
      )}

      {editingDept && (
        <DepartmentFormModal
          department={department}
          onClose={() => setEditingDept(false)}
          onSaved={updated => { onDepartmentUpdated(updated); setEditingDept(false); }}
        />
      )}
    </div>
  );
}

function DepartmentsSection() {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [formFor, setFormFor]         = useState(null); // null | 'new' | department
  const [selectedDept, setSelectedDept] = useState(null);
  const [search, setSearch]           = useState('');

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getDepartments();
      const data = res?.data ?? res;
      setDepartments(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDepartments(); }, [loadDepartments]);

  async function handleDelete(dept) {
    const ok = await confirm({
      title: `Delete ${dept.name}?`,
      message: 'The department is removed. Its members keep their accounts.',
      confirmLabel: 'Delete department',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteDepartment(dept.id);
      setDepartments(prev => prev.filter(d => d.id !== dept.id));
      toast.success('Department deleted');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to delete department'));
    }
  }

  if (selectedDept) {
    return (
      <DepartmentDetail
        department={selectedDept}
        onBack={() => { setSelectedDept(null); loadDepartments(); }}
        onDepartmentUpdated={updated => {
          setDepartments(prev => prev.map(d => (d.id === updated.id ? { ...d, ...updated } : d)));
          setSelectedDept(prev => ({ ...prev, ...updated }));
        }}
      />
    );
  }

  const q = search.trim().toLowerCase();
  const visible = departments.filter(d => !q || d.name?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q));
  const totalMembers = departments.reduce((sum, d) => sum + (Number(d.memberCount) || 0), 0);

  return (
    <div>
      <SectionHeader
        title="Departments"
        description="Organise your company members into departments."
        action={<Button variant="primary" icon={Plus} onClick={() => setFormFor('new')}>Add department</Button>}
      />

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-28 animate-pulse rounded-2xl bg-sand-50" />)}
        </div>
      ) : departments.length === 0 ? (
        <EmptyState icon={Building2} title="No departments yet" message="Departments help you group people and see who's off in each team.">
          <Button variant="primary" icon={Plus} onClick={() => setFormFor('new')}>Create your first department</Button>
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[13px] text-sand-600">
              <span className="font-semibold text-sand-900">{departments.length}</span> {departments.length === 1 ? 'department' : 'departments'}
              {totalMembers > 0 && <> · <span className="font-semibold text-sand-900">{totalMembers}</span> memberships</>}
            </span>
            {departments.length > 4 && (
              <SearchInput value={search} onChange={setSearch} placeholder="Search departments…" className="w-full sm:w-64" />
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {visible.map(dept => (
              <div
                key={dept.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedDept(dept)}
                onKeyDown={e => { if (e.key === 'Enter') setSelectedDept(dept); }}
                className="ph-fade group flex cursor-pointer items-start gap-3 rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-4 transition-all hover:-translate-y-0.5 hover:border-sand-300 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              >
                <DepartmentIcon name={dept.name} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold text-sand-900">{dept.name}</div>
                  <div className="mt-0.5 line-clamp-2 text-[13px] text-sand-600">{dept.description || 'No description'}</div>
                  {dept.memberCount !== undefined && (
                    <div className="mt-2.5">
                      <Chip tone={dept.memberCount ? 'ink' : 'sand'}><Users size={10} /> {plural(Number(dept.memberCount) || 0, 'member')}</Chip>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  <Menu
                    trigger={({ toggle }) => (
                      <IconButton label="More actions" onClick={toggle} className="sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:focus:opacity-100">
                        <MoreHorizontal size={17} />
                      </IconButton>
                    )}
                    items={[
                      { label: 'Open', icon: ChevronRight, onClick: () => setSelectedDept(dept) },
                      { label: 'Edit details', icon: Edit2, onClick: () => setFormFor(dept) },
                      { label: 'Delete', icon: Trash2, tone: 'danger', onClick: () => handleDelete(dept) },
                    ]}
                  />
                  <ChevronRight size={18} className="text-sand-300 transition-all group-hover:translate-x-0.5 group-hover:text-sand-600" />
                </div>
              </div>
            ))}
            {!q && (
              <button
                type="button"
                onClick={() => setFormFor('new')}
                className="group flex min-h-[104px] items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-sand-200 p-4 transition-all hover:border-ink/40 hover:bg-ink-50/40"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sand-100 text-sand-600 transition-all group-hover:scale-110 group-hover:bg-ink group-hover:text-[color:var(--on-accent)]">
                  <Plus size={18} />
                </span>
                <span className="text-sm font-semibold text-sand-700 group-hover:text-ink">New department</span>
              </button>
            )}
          </div>
          {q && visible.length === 0 && <div className="py-10 text-center text-sm text-sand-500">No departments match “{search}”</div>}
        </>
      )}

      {formFor && (
        <DepartmentFormModal
          department={formFor === 'new' ? null : formFor}
          onClose={() => setFormFor(null)}
          onSaved={saved => {
            if (!saved?.id) { loadDepartments(); return; }
            setDepartments(prev => (prev.some(d => d.id === saved.id)
              ? prev.map(d => (d.id === saved.id ? { ...d, ...saved } : d))
              : [...prev, saved]));
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MEMBERS
// ═════════════════════════════════════════════════════════════
function AllowanceModal({ user, onClose, onSaved }) {
  const [unit, setUnit]       = useState(user.allowanceUnit ?? 'days');
  const [balance, setBalance] = useState(user.annualLeaveBalance ?? 25);
  const [saving, setSaving]   = useState(false);
  const used = Number(user.annualLeaveUsed ?? 0);
  const remaining = Math.max(0, Number(balance) - used);

  async function handleSave() {
    setSaving(true);
    try {
      await apiService.updateUserAllowance(user.id, { allowanceUnit: unit, annualLeaveBalance: balance });
      toast.success(`Allowance updated for ${user.firstName ?? user.name}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to update allowance.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-md" z={300} labelledBy="allow-title">
      <ModalHeader
        id="allow-title"
        title="Edit allowance"
        subtitle={<span className="inline-flex items-center gap-2">{fullName(user)} · <UnitBadge unit={user.allowanceUnit} /></span>}
        onClose={onClose}
        leading={<Avatar person={user} size={40} />}
      />
      <ModalBody className="space-y-5">
        <div>
          <label className={labelClass}>Allowance unit</label>
          <Segmented
            size="md"
            className="flex w-full"
            value={unit}
            onChange={setUnit}
            options={[
              { value: 'days', label: 'Days', icon: CalendarDays },
              { value: 'hours', label: 'Hours', icon: Clock },
            ]}
          />
        </div>
        <div>
          <label className={labelClass}>Annual allowance</label>
          <NumberInput value={balance} onChange={setBalance} min={0} max={unit === 'days' ? 365 : 9999} unit={unit} />
        </div>
        <div className="grid grid-cols-3 gap-2 rounded-2xl bg-sand-50 p-3 text-center">
          <div>
            <div className="text-lg font-semibold text-sand-900">{balance}</div>
            <div className="text-[11px] font-medium text-sand-500">Allowance</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-sand-900">{used}</div>
            <div className="text-[11px] font-medium text-sand-500">Used</div>
          </div>
          <div>
            <div className={cx('text-lg font-semibold', remaining === 0 ? 'text-[color:var(--bad-text)]' : 'text-[color:var(--ok-text)]')}>{remaining}</div>
            <div className="text-[11px] font-medium text-sand-500">Remaining</div>
          </div>
        </div>
        {Number(balance) < used && (
          <Notice tone="warn">This is less than they've already used ({used} {unit}).</Notice>
        )}
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" icon={Check} loading={saving} onClick={handleSave}>Save allowance</Button>
      </ModalFooter>
    </Modal>
  );
}

function RoleModal({ user, onClose, onSaved }) {
  const [role, setRole]     = useState(user.role);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!role || role === user.role) { onClose(); return; }
    setSaving(true);
    try {
      await apiService.updateUserRole(user.id, role);
      toast.success(`${user.firstName ?? fullName(user)} is now ${roleInfo(role).label === 'Admin' ? 'an' : 'a'} ${roleInfo(role).label}`);
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to update role');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} className="max-w-md" z={300} labelledBy="role-title">
      <ModalHeader id="role-title" title="Change role" subtitle={fullName(user)} onClose={onClose} leading={<Avatar person={user} size={40} />} />
      <ModalBody className="space-y-2">
        {ROLES.map(r => {
          const on = role === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={cx(
                'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all',
                on ? 'border-ink bg-ink-50 ring-2 ring-ink/10' : 'border-sand-200 hover:border-sand-300 hover:bg-sand-50',
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${r.color}14`, color: r.color }}>
                <r.icon size={18} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-sand-900">
                  {r.label}
                  {user.role === r.value && <Chip tone="sand">Current</Chip>}
                </span>
                <span className="block text-xs text-sand-600">{r.desc}</span>
              </span>
              <span className={cx('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border', on ? 'border-ink bg-ink text-[color:var(--on-accent)]' : 'border-sand-300')}>
                {on && <Check size={12} />}
              </span>
            </button>
          );
        })}
      </ModalBody>
      <ModalFooter className="justify-end">
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" icon={Check} loading={saving} disabled={role === user.role} onClick={handleSave}>Save role</Button>
      </ModalFooter>
    </Modal>
  );
}

function AllowanceBar({ user }) {
  const total = Number(user.annualLeaveBalance ?? 25);
  const used = Number(user.annualLeaveUsed ?? 0);
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const unit = user.allowanceUnit === 'hours' ? 'h' : 'd';
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="font-semibold text-sand-900">{Math.max(0, total - used)}{unit} <span className="font-normal text-sand-500">left</span></span>
        <span className="text-xs text-sand-500">{used}/{total}{unit}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-sand-100">
        <div
          className={cx('h-full rounded-full transition-all', pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-ink')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, active, onClick, tone = 'ink' }) {
  const tones = { ink: 'text-ink bg-ink-50', amber: 'text-[color:var(--warn-text)] bg-[color:var(--warn-bg)]', green: 'text-[color:var(--ok-text)] bg-[color:var(--ok-bg)]', sand: 'text-sand-700 bg-sand-100' };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'flex items-center gap-3 rounded-2xl border p-3 text-left transition-all',
        active ? 'border-ink bg-[color:var(--surface)] shadow-md ring-2 ring-ink/10' : 'border-sand-200 bg-[color:var(--surface)] hover:border-sand-300 hover:shadow-sm',
      )}
    >
      <span className={cx('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', tones[tone])}><Icon size={17} /></span>
      <span className="min-w-0">
        <span className="block text-lg font-semibold leading-tight text-sand-900">{value}</span>
        <span className="block truncate text-xs text-sand-600">{label}</span>
      </span>
    </button>
  );
}

function MembersSection({ onInvite }) {
  const confirm = useConfirm();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [roleUser, setRoleUser]   = useState(null);
  const [allowUser, setAllowUser] = useState(null);
  const currentUser = tokenManager.getUser();

  const load = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    try {
      const res = await apiService.getCompanyUsers();
      setUsers(unwrapList(res, 'users'));
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: users.length,
    admins: users.filter(u => u.role === 'admin' || u.role === 'super_admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    pending: users.filter(u => !u.isEmailVerified).length,
  }), [users]);

  const filtered = users.filter(u => {
    if (filter === 'admins' && !(u.role === 'admin' || u.role === 'super_admin')) return false;
    if (filter === 'managers' && u.role !== 'manager') return false;
    if (filter === 'pending' && u.isEmailVerified) return false;
    const q = search.trim().toLowerCase();
    return !q || fullName(u).toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  async function handleDelete(user) {
    const ok = await confirm({
      title: `Remove ${fullName(user)}?`,
      message: 'They will lose access to the company and their leave history will no longer be shown.',
      confirmLabel: 'Remove user',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteUser(user.id);
      toast.success('User removed');
      load(true);
    } catch {
      toast.error('Failed to remove user');
    }
  }

  const gridCols = 'md:grid-cols-[minmax(0,1.7fr)_120px_minmax(150px,1fr)_92px_40px]';

  return (
    <div>
      <SectionHeader
        title="Members"
        description="Each member can have their own allowance unit (days or hours) and balance."
        action={
          <>
            <IconButton label="Refresh" onClick={() => load(true)} disabled={refreshing}>
              <RefreshCw size={16} className={cx(refreshing && 'animate-spin')} />
            </IconButton>
            {onInvite && <Button variant="primary" icon={UserPlus} onClick={onInvite}>Invite people</Button>}
          </>
        }
      />

      {/* Stat tiles double as filters */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="All members" value={counts.all} icon={Users} active={filter === 'all'} onClick={() => setFilter('all')} />
        <StatTile label="Admins" value={counts.admins} icon={Shield} active={filter === 'admins'} onClick={() => setFilter(f => (f === 'admins' ? 'all' : 'admins'))} />
        <StatTile label="Managers" value={counts.managers} icon={Building2} tone="green" active={filter === 'managers'} onClick={() => setFilter(f => (f === 'managers' ? 'all' : 'managers'))} />
        <StatTile label="Not verified yet" value={counts.pending} icon={Hourglass} tone="amber" active={filter === 'pending'} onClick={() => setFilter(f => (f === 'pending' ? 'all' : 'pending'))} />
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email…" className="mb-3" />

      {loading ? (
        <SkeletonRows rows={5} height="h-16" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sand-200">
          <div className={cx('hidden gap-4 border-b border-sand-200 bg-sand-50 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-sand-500 md:grid', gridCols)}>
            <span>Member</span><span>Role</span><span>Allowance</span><span>Status</span><span />
          </div>

          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-sand-500">
              {search || filter !== 'all' ? 'No members match these filters' : 'No members yet'}
            </div>
          ) : (
            <div className="divide-y divide-sand-100">
              {filtered.map(user => {
                const isMe = user.id === currentUser?.id;
                return (
                  <div
                    key={user.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setAllowUser(user)}
                    onKeyDown={e => { if (e.key === 'Enter') setAllowUser(user); }}
                    title="Edit allowance"
                    className={cx('group grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 px-4 py-3 transition-colors hover:bg-sand-50 focus-visible:bg-sand-50 focus-visible:outline-none', gridCols)}
                  >
                    {/* Member */}
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar person={user} size={36} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-sand-900">{fullName(user)}</span>
                          {isMe && <Chip tone="ink">You</Chip>}
                        </div>
                        <div className="truncate text-xs text-sand-500">{user.email}</div>
                      </div>
                    </div>

                    {/* Menu (top-right on mobile, last column on desktop) */}
                    <div className="flex justify-end md:order-last" onClick={e => e.stopPropagation()}>
                      <Menu
                        trigger={({ toggle }) => <IconButton label="Actions" onClick={toggle}><MoreHorizontal size={17} /></IconButton>}
                        items={[
                          { label: 'Edit allowance', hint: 'Unit and yearly balance', icon: Wallet, onClick: () => setAllowUser(user) },
                          !isMe && { label: 'Change role', hint: `Currently ${roleInfo(user.role).label}`, icon: Shield, onClick: () => setRoleUser(user) },
                          !isMe && { label: 'Remove user', icon: Trash2, tone: 'danger', onClick: () => handleDelete(user) },
                        ]}
                      />
                    </div>

                    {/* Role + status (inline row on mobile) */}
                    <div className="col-span-2 flex flex-wrap items-center gap-2 md:col-span-1">
                      <RoleBadge role={user.role} />
                      <span className="md:hidden"><UnitBadge unit={user.allowanceUnit} /></span>
                    </div>

                    <div className="col-span-2 md:col-span-1"><AllowanceBar user={user} /></div>

                    <div className="hidden md:block">
                      {user.status && user.status !== 'active'
                        ? <Chip tone="red"><Power size={10} /> {user.status === 'archived' ? 'Archived' : 'Deactivated'}</Chip>
                        : user.isEmailVerified
                          ? <Chip tone="green"><Check size={10} /> Verified</Chip>
                          : <Chip tone="amber"><Hourglass size={10} /> Pending</Chip>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <p className="m-0 mt-3 text-xs text-sand-500">Tip: click anyone to edit their allowance.</p>
      )}

      {roleUser && <RoleModal user={roleUser} onClose={() => setRoleUser(null)} onSaved={() => load(true)} />}
      {allowUser && <AllowanceModal user={allowUser} onClose={() => setAllowUser(null)} onSaved={() => load(true)} />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// INVITE
// ═════════════════════════════════════════════════════════════
function splitEmails(text) {
  return text.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
}

function EmailChipsInput({ emails, onChange, draft, onDraftChange }) {
  const inputRef = useRef(null);

  const commit = text => {
    const parts = splitEmails(text);
    if (!parts.length) return;
    onChange([...new Set([...emails, ...parts])]);
    onDraftChange('');
  };

  function handleKeyDown(e) {
    if (['Enter', ',', ';', ' '].includes(e.key) || (e.key === 'Tab' && draft.trim())) {
      if (draft.trim()) { e.preventDefault(); commit(draft); }
      else if (e.key !== 'Tab') e.preventDefault();
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      onChange(emails.slice(0, -1));
    }
  }

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="flex min-h-[88px] cursor-text flex-wrap content-start gap-1.5 rounded-xl border border-sand-200 bg-[color:var(--surface)] p-2 transition focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/15"
    >
      {emails.map(email => {
        const valid = EMAIL_RE.test(email);
        return (
          <span
            key={email}
            title={valid ? email : 'Not a valid email address'}
            className={cx(
              'ph-pop inline-flex h-7 items-center gap-1.5 rounded-lg pl-2.5 pr-1 text-[13px] font-medium',
              valid ? 'bg-ink-50 text-ink' : 'bg-[color:var(--bad-bg)] text-[color:var(--bad-text)] line-through decoration-red-300',
            )}
          >
            {valid ? <Mail size={12} /> : null}
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              onClick={e => { e.stopPropagation(); onChange(emails.filter(x => x !== email)); }}
              className="flex h-5 w-5 items-center justify-center rounded-md hover:bg-[color:var(--hover)]"
            >
              <X size={12} />
            </button>
          </span>
        );
      })}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={e => onDraftChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => commit(draft)}
        onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (/[\s,;]/.test(text)) { e.preventDefault(); commit(`${draft} ${text}`); }
        }}
        placeholder={emails.length ? 'Add another…' : 'alice@company.com, bob@company.com'}
        className="h-7 min-w-[180px] flex-1 border-none bg-transparent px-1.5 text-sm text-sand-900 outline-none placeholder:text-sand-500"
      />
    </div>
  );
}

function InviteSection() {
  const confirm = useConfirm();
  const [emails, setEmails]           = useState([]);
  const [draft, setDraft]             = useState('');
  const [role, setRole]               = useState('member');
  const [message, setMessage]         = useState('');
  const [sending, setSending]         = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [actionId, setActionId]       = useState(null);

  const roleOptions = ROLES.filter(r => r.value !== 'super_admin');

  const loadInvitations = useCallback(async () => {
    try {
      setLoadingList(true);
      const res = await apiService.listInvitations({ status: 'pending' });
      setInvitations(unwrapList(res, 'invitations'));
    } catch {
      toast.error('Failed to load invitations');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadInvitations(); }, [loadInvitations]);

  const allEmails   = [...new Set([...emails, ...splitEmails(draft)])];
  const validEmails = allEmails.filter(e => EMAIL_RE.test(e));
  const invalidCount = allEmails.length - validEmails.length;

  async function handleSend() {
    if (validEmails.length === 0) { toast.error('Enter at least one valid email address'); return; }
    setSending(true);
    try {
      await apiService.sendInvitations(validEmails, role, message.trim());
      toast.success(`${validEmails.length > 1 ? `${validEmails.length} invitations` : 'Invitation'} sent`);
      setEmails(allEmails.filter(e => !EMAIL_RE.test(e))); // keep only the ones that need fixing
      setDraft('');
      setMessage('');
      loadInvitations();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to send invitations'));
    } finally {
      setSending(false);
    }
  }

  async function handleResend(id) {
    setActionId(id);
    try {
      await apiService.resendInvitation(id);
      toast.success('Invitation resent');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to resend'));
    } finally {
      setActionId(null);
    }
  }

  async function handleCancel(inv) {
    const ok = await confirm({
      title: 'Cancel this invitation?',
      message: `The link sent to ${inv.email} will stop working.`,
      confirmLabel: 'Cancel invitation',
      cancelLabel: 'Keep it',
      tone: 'danger',
    });
    if (!ok) return;
    setActionId(inv.id);
    try {
      await apiService.cancelInvitation(inv.id);
      toast.success('Invitation cancelled');
      setInvitations(prev => prev.filter(i => i.id !== inv.id));
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to cancel'));
    } finally {
      setActionId(null);
    }
  }

  return (
    <div>
      <SectionHeader
        title="Invite members"
        description="Invite people to join your company. They'll receive an email with a link to set up their account."
      />

      <div className="max-w-3xl space-y-6">
        {/* Send form */}
        <div className="rounded-2xl border border-sand-200 p-5 sm:p-6">
          <div className="space-y-5">
            <div>
              <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <label className="text-[13px] font-semibold text-sand-700">Email addresses</label>
                {allEmails.length > 0 && (
                  <span className="text-xs text-sand-600">
                    <span className="font-semibold text-ink">{validEmails.length} ready</span>
                    {invalidCount > 0 && <span className="text-[color:var(--bad-text)]"> · {invalidCount} to fix</span>}
                  </span>
                )}
              </div>
              <EmailChipsInput emails={emails} onChange={setEmails} draft={draft} onDraftChange={setDraft} />
              <p className="m-0 mt-1.5 text-xs text-sand-500">Press Enter, comma or space after each address — or paste a whole list.</p>
            </div>

            <div>
              <label className={labelClass}>Invite as</label>
              <div className="grid gap-2 sm:grid-cols-3">
                {roleOptions.map(r => {
                  const on = role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setRole(r.value)}
                      className={cx(
                        'flex items-start gap-2.5 rounded-xl border p-3 text-left transition-all',
                        on ? 'border-ink bg-ink-50 ring-2 ring-ink/10' : 'border-sand-200 hover:border-sand-300 hover:bg-sand-50',
                      )}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${r.color}14`, color: r.color }}>
                        <r.icon size={15} />
                      </span>
                      <span className="min-w-0">
                        <span className={cx('block text-sm font-semibold', on ? 'text-ink' : 'text-sand-900')}>{r.label}</span>
                        <span className="block text-xs leading-snug text-sand-600">{r.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <label htmlFor="invite-msg" className="text-[13px] font-semibold text-sand-700">
                  Personal message <span className="font-normal text-sand-500">(optional)</span>
                </label>
                {message && <span className="text-xs text-sand-500">{message.length} characters</span>}
              </div>
              <textarea
                id="invite-msg"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Add a personal note to the invitation email…"
                rows={2}
                className={textareaClass}
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-sand-100 pt-4">
            <span className="text-[13px] text-sand-600">
              {validEmails.length
                ? <>Sending to <span className="font-semibold text-sand-900">{plural(validEmails.length, 'person', 'people')}</span> as <span className="font-semibold text-sand-900">{roleInfo(role).label}</span></>
                : 'Add at least one email address'}
            </span>
            <Button variant="primary" icon={Send} loading={sending} disabled={validEmails.length === 0} onClick={handleSend}>
              Send invitation{validEmails.length > 1 ? `s (${validEmails.length})` : ''}
            </Button>
          </div>
        </div>

        {/* Pending invitations */}
        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="m-0 flex items-center gap-2 text-[15px] font-semibold text-sand-900">
              Pending invitations
              {invitations.length > 0 && <Chip tone="sand">{invitations.length}</Chip>}
            </h3>
            <Button size="sm" icon={RefreshCw} onClick={loadInvitations} disabled={loadingList}>Refresh</Button>
          </div>

          {loadingList ? (
            <SkeletonRows rows={3} />
          ) : invitations.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-sand-200">
              <EmptyState icon={Mail} title="No pending invitations" message="Invitations you send will show here until they're accepted." />
            </div>
          ) : (
            <div className="divide-y divide-sand-100 rounded-2xl border border-sand-200">
              {invitations.map(inv => {
                const busy = actionId === inv.id;
                const expires = inv.expiresAt ? new Date(inv.expiresAt) : null;
                const daysLeft = expires ? Math.ceil((expires - new Date()) / 86400000) : null;
                return (
                  <div key={inv.id} className="ph-fade flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sand-100 text-sand-500">
                      <Mail size={15} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-sand-900">{inv.email}</div>
                      <div className="text-xs text-sand-500">
                        Sent {formatShortDate(inv.createdAt) || '—'}
                        {expires && ` · expires ${expires.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                      </div>
                    </div>
                    <RoleBadge role={inv.role ?? 'member'} />
                    {daysLeft != null && daysLeft <= 2
                      ? <Chip tone={daysLeft <= 0 ? 'red' : 'amber'}>{daysLeft <= 0 ? 'Expired' : `${plural(daysLeft, 'day')} left`}</Chip>
                      : <Chip tone="amber">Pending</Chip>}
                    <div className="flex gap-0.5">
                      <IconButton label="Resend invitation" onClick={() => handleResend(inv.id)} disabled={busy}>
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                      </IconButton>
                      <IconButton label="Cancel invitation" tone="danger" onClick={() => handleCancel(inv)} disabled={busy}>
                        <X size={15} />
                      </IconButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// ROLES
// ═════════════════════════════════════════════════════════════
function RolesSection({ onManage }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setUsers(unwrapList(await apiService.getCompanyUsers(), 'users'));
      } catch {
        /* counts are a nice-to-have */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <SectionHeader
        title="Roles"
        description="Overview of roles and their permissions."
        action={onManage && <Button icon={Users} onClick={onManage}>Manage members</Button>}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {ROLES.map(role => {
          const people = users.filter(u => u.role === role.value);
          return (
            <div key={role.value} className="ph-fade flex flex-col rounded-2xl border border-sand-200 p-5 transition-all hover:border-sand-300 hover:shadow-md">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${role.color}14`, color: role.color }}>
                  <role.icon size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold text-sand-900">{role.label}</div>
                  <p className="m-0 mt-0.5 text-[13px] leading-relaxed text-sand-600">{role.desc}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-sand-100 pt-3">
                <span className="text-[13px] text-sand-600">
                  {loading ? '…' : people.length ? plural(people.length, 'person', 'people') : 'No one yet'}
                </span>
                <AvatarStack members={people} max={5} size={24} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlaceholderSection({ title }) {
  return (
    <div>
      <SectionHeader title={title} />
      <div className="rounded-2xl border-2 border-dashed border-sand-200">
        <EmptyState icon={SlidersHorizontal} title={`${title} settings coming soon`} />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN SETTINGS PAGE
// ═════════════════════════════════════════════════════════════
function sectionFromHash() {
  const id = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
  return NAV_ITEMS.some(i => i.id === id) ? id : 'general';
}

export default function Settings() {
  const [activeSection, setActiveSection] = useState(sectionFromHash);
  const { settings, loading, save }       = useCompanySettings();

  // Keep the open section in the URL so refresh / back keeps your place
  useEffect(() => {
    try { window.history.replaceState(null, '', `#${activeSection}`); } catch { /* ignore */ }
  }, [activeSection]);

  useEffect(() => {
    const onHash = () => setActiveSection(sectionFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  function renderSection() {
    if ((loading || !settings) && ['general', 'carry-forward'].includes(activeSection)) {
      return (
        <div className="space-y-4">
          <div className="h-16 animate-pulse rounded-xl bg-sand-50" />
          <div className="h-28 animate-pulse rounded-2xl bg-sand-50" />
          <SkeletonRows rows={3} height="h-16" />
        </div>
      );
    }

    switch (activeSection) {
      case 'general':         return <GeneralSection settings={settings} onSave={save} />;
      case 'carry-forward':   return <CarryForwardSection settings={settings} onSave={save} />;
      case 'members':         return <MembersSection onInvite={() => setActiveSection('invite')} />;
      case 'roles':           return <RolesSection onManage={() => setActiveSection('members')} />;
      case 'leave-types':     return <LeaveTypesSection />;
      case 'departments':     return <DepartmentsSection />;
      case 'public-holidays': return <PublicHolidaysSection />;
      case 'shutdowns':       return <ShutdownsSection />;
      case 'leave-policies':  return <LeavePoliciesSection />;
      case 'billing':         return <BillingSection />;
      case 'invite':          return <InviteSection />;
      case 'teams':           return <PlaceholderSection title="Teams" />;
      default:                return null;
    }
  }

  return (
    <ConfirmProvider>
      <UIStyles />
      <div className="mx-auto w-full max-w-6xl">
        <header className="mb-5 sm:mb-6">
          <h1 className="m-0 text-2xl font-semibold tracking-tight text-sand-900">Settings</h1>
          <p className="m-0 mt-1 text-sm text-sand-600">Leave rules, holidays and people for your company.</p>
        </header>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <nav aria-label="Settings sections" className="lg:sticky lg:top-6 lg:w-64 lg:shrink-0">
            {/* Small screens: scrollable tabs */}
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:hidden">
              {NAV_ITEMS.map(item => {
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setActiveSection(item.id)}
                    className={cx(
                      'flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-medium transition-all',
                      active
                        ? 'border-ink bg-ink text-[color:var(--on-accent)] shadow-sm'
                        : 'border-sand-200 bg-[color:var(--surface)] text-sand-700 hover:border-sand-300 hover:bg-sand-100',
                    )}
                  >
                    <item.icon size={15} />
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Large screens: grouped side menu */}
            <div className="hidden rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-2 lg:block">
              {NAV_GROUPS.map((group, gi) => (
                <div key={group.label} className={gi > 0 ? 'mt-2 border-t border-sand-100 pt-2' : ''}>
                  <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">{group.label}</div>
                  {group.items.map(item => {
                    const active = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-current={active ? 'page' : undefined}
                        onClick={() => setActiveSection(item.id)}
                        className={cx(
                          'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all',
                          active ? 'bg-ink-50' : 'hover:bg-sand-100',
                        )}
                      >
                        <span
                          className={cx(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all',
                            active ? 'bg-ink text-[color:var(--on-accent)] shadow-sm' : 'bg-sand-100 text-sand-600 group-hover:bg-[color:var(--surface)] group-hover:text-sand-900',
                          )}
                        >
                          <item.icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cx('block text-sm', active ? 'font-semibold text-ink' : 'font-medium text-sand-900')}>{item.label}</span>
                          <span className="block truncate text-xs text-sand-600">{item.desc}</span>
                        </span>
                        <ChevronRight
                          size={14}
                          className={cx('shrink-0 transition-all', active ? 'text-ink' : 'text-transparent group-hover:translate-x-0.5 group-hover:text-sand-500')}
                        />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </nav>

          <section className="min-w-0 flex-1 rounded-2xl border border-sand-200 bg-[color:var(--surface)] p-4 sm:p-7">
            <div key={activeSection} className="ph-rise">
              {renderSection()}
            </div>
          </section>
        </div>
      </div>
    </ConfirmProvider>
  );
}